import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import type { JustificatifEntrant } from '../../types/ndf';
import { BoutonPrincipal, BoutonSecondaire } from '../../components/ndf/ui/Champ';
import {
  erreurGlobale,
  erreursParChamp,
  messageRefusPiece,
  type ErreursChamps,
} from '../../components/ndf/ui/erreurs-champs';
import { refusDuTypeDePiece } from '../../lib/piece-jointe';
import { apercuDe, preparerJustificatif } from '../../components/ndf/photo';
import { AnalyseEnCours } from '../../components/ndf/AnalyseEnCours';
import {
  champsVides,
  FormulaireDepense,
  type ChampsDepense,
} from '../../components/ndf/FormulaireDepense';
import { prefillDepuisExtraction, type Prefill } from '../../lib/ndf-prefill';
import { useToast } from '../../context/ToastContext';

/**
 * Saisie d'une nouvelle dépense.
 *
 * Trois temps : on photographie le ticket, on regarde Gertrude le lire, on
 * corrige un formulaire déjà rempli. C'est ce qui tient l'objectif des trente
 * secondes — le salarié relit, il ne saisit plus.
 *
 * Le formulaire n'apparaît qu'une fois la lecture finie : ses champs prennent
 * leur valeur à l'affichage, et rien ne doit venir écraser une saisie en cours.
 * Si la lecture échoue, on bascule en saisie manuelle AVEC la photo conservée
 * (C2.4) : un échec d'analyse ne coûte jamais le justificatif.
 */
export function NdfDepenseFormPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const champFichier = useRef<HTMLInputElement>(null);

  const { data: categoriesData } = useQuery({
    queryKey: ['ndf', 'categories'],
    queryFn: () => ndfApi.categories.lire(),
  });
  const categories = (categoriesData?.categories ?? []).filter((c) => c.actif);

  const [pieces, setPieces] = useState<JustificatifEntrant[]>([]);
  const [preparation, setPreparation] = useState(false);
  const [erreurs, setErreurs] = useState<ErreursChamps>({});
  const [analyseId, setAnalyseId] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  const [risque, setRisque] = useState<string | null>(null);
  // Ce que le modèle a répondu, mot pour mot : c'est la seule façon de
  // comprendre, des mois plus tard, pourquoi un champ avait été rempli ainsi.
  const [extractionBrute, setExtractionBrute] = useState<string | null>(null);
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);

  // Suivi de l'analyse : on interroge toutes les 2 s tant qu'elle tourne, comme
  // le fait déjà l'extraction des factures fournisseurs.
  useQuery({
    queryKey: ['ndf', 'analyse', analyseId],
    queryFn: async () => {
      const res = await ndfApi.analyses.suivre(analyseId!);
      if (res.statut === 'terminee' && res.champs) {
        setPrefill(prefillDepuisExtraction(res.champs, categories));
        setRisque(res.justificatif_a_risque?.raison ?? null);
        setExtractionBrute(res.extraction_brute ?? null);
        setAnalyseId(null);
        setFormulaireOuvert(true);
      } else if (res.statut === 'echec') {
        setAnalyseId(null);
        setFormulaireOuvert(true);
        toast.info(
          'La lecture automatique n’a pas abouti. Votre justificatif est conservé : complétez les champs à la main.',
        );
      }
      return res;
    },
    enabled: Boolean(analyseId),
    refetchInterval: 2000,
    retry: false,
  });

  const enregistrer = useMutation({
    mutationFn: (champs: ChampsDepense) =>
      ndfApi.depenses.creer({
        ...champs,
        sous_categorie_id: champs.sous_categorie_id || null,
        description: champs.description || null,
        justificatifs: pieces,
        // Ce que l'IA a lu, conservé à côté de ce que le salarié a validé :
        // c'est l'écart entre les deux qui déclenche les contrôles.
        ...(prefill?.lecture ?? {}),
        extraction_brute: extractionBrute,
      }),
    onSuccess: (res) => {
      setErreurs({});
      toast.success('Dépense enregistrée.');
      for (const message of res.forcages ?? []) toast.info(message);
      qc.invalidateQueries({ queryKey: ['ndf', 'depenses'] });
      navigate('/notes-de-frais/mes-depenses');
    },
    onError: (err) => {
      setErreurs(erreursParChamp(err));
      // `messageRefusPiece` AVANT le message global : un refus de justificatif
      // porte le chemin `justificatifs[n].mime`, que `erreurGlobale` ne remonte
      // pas (elle ne rend que les erreurs SANS chemin) et qu'aucun champ de ce
      // formulaire n'affiche. Sans lui, le salarié cliquait « Enregistrer » et
      // RIEN ne se passait — le refus silencieux que ce fichier d'aide appelle
      // « le pire cas possible ». Relevé à l'audit du 14/10/2026, le jour même
      // où la garde de type a été étendue aux notes de frais.
      const global = messageRefusPiece(err) ?? erreurGlobale(err);
      if (global) toast.error(global);
      // La saisie reste à l'écran : rien n'est perdu, on corrige et on renvoie.
    },
  });

  async function ajouterFichiers(fichiers: FileList | null) {
    if (!fichiers?.length) return;
    setPreparation(true);
    try {
      const prets: JustificatifEntrant[] = [];
      for (const fichier of Array.from(fichiers)) {
        // Le serveur refuse un fichier qui s'exécuterait à la réouverture. Le
        // dire ICI évite de préparer puis d'envoyer une photo pour rien, et
        // c'est la MÊME fonction que le serveur appelle.
        const refus = refusDuTypeDePiece(fichier);
        if (refus) {
          toast.error(refus);
          continue;
        }
        prets.push(await preparerJustificatif(fichier));
      }
      if (prets.length === 0) return;
      const toutes = [...pieces, ...prets];
      setPieces(toutes);

      // Une photo ajoutée APRÈS l'ouverture du formulaire ne relance pas la
      // lecture : elle écraserait ce que le salarié vient de corriger.
      if (formulaireOuvert) {
        toast.success('Photo ajoutée au justificatif.');
        return;
      }
      const { id } = await ndfApi.analyses.lancer(toutes);
      setAnalyseId(id);
    } catch (err) {
      // Le refus de type d'abord : le repli invite à « réessayer », ce qui est
      // un mauvais conseil pour un fichier qui sera refusé à l'identique.
      toast.error(
        messageRefusPiece(err) ??
          erreurGlobale(err) ??
          'Cette photo n’a pas pu être préparée. Réessayez ou prenez-la à nouveau.',
      );
      // Le fichier reste attaché : on ouvre le formulaire plutôt que de laisser
      // le salarié devant un écran qui n'a rien fait.
      setFormulaireOuvert(true);
    } finally {
      setPreparation(false);
    }
  }

  const champFichierCache = (
    <input
      ref={champFichier}
      type="file"
      accept="image/*,application/pdf"
      capture="environment"
      multiple
      className="hidden"
      onChange={(e) => {
        void ajouterFichiers(e.target.files);
        e.target.value = '';
      }}
    />
  );

  // ─── 1. Le point de départ : photographier, ou se passer de justificatif ───
  if (!formulaireOuvert && !analyseId) {
    return (
      <div className="space-y-4">
        <header className="flex items-center justify-between gap-2">
          <h1 className="font-titre text-lg font-bold text-nuit">Nouvelle dépense</h1>
          <BoutonSecondaire type="button" onClick={() => navigate(-1)}>
            Annuler
          </BoutonSecondaire>
        </header>

        {champFichierCache}

        <div className="rounded-lg border border-nuit/10 bg-white p-6 text-center">
          <p className="mb-4 text-sm text-nuit/70">
            Photographiez votre ticket : Gertrude le lit et remplit les champs pour vous.
          </p>
          <BoutonPrincipal
            type="button"
            disabled={preparation}
            onClick={() => champFichier.current?.click()}
            className="w-full"
          >
            {preparation ? 'Préparation…' : 'Photographier le ticket'}
          </BoutonPrincipal>
        </div>

        {/* Actions secondaires, sans concurrence visuelle avec la précédente. */}
        <p className="text-center">
          <button
            type="button"
            onClick={() => setFormulaireOuvert(true)}
            className="min-h-11 text-sm text-nuit underline"
          >
            Saisir sans justificatif
          </button>
        </p>
        {/* Un trajet n'a pas de ticket à photographier : son montant vient du
            barème kilométrique, pas d'un justificatif. */}
        <p className="text-center">
          <button
            type="button"
            onClick={() => navigate('/notes-de-frais/trajet/nouveau')}
            className="min-h-11 text-sm text-nuit underline"
          >
            Déclarer un trajet en véhicule personnel
          </button>
        </p>
      </div>
    );
  }

  // ─── 2. L'attente : on montre le ticket en cours de lecture ────────────────
  if (analyseId) {
    return (
      <div className="space-y-4">
        <h1 className="font-titre text-lg font-bold text-nuit">Nouvelle dépense</h1>
        <AnalyseEnCours piece={pieces[0]} />
        <p className="text-center">
          <button
            type="button"
            onClick={() => {
              setAnalyseId(null);
              setFormulaireOuvert(true);
            }}
            className="min-h-11 text-sm text-nuit underline"
          >
            Remplir moi-même sans attendre
          </button>
        </p>
      </div>
    );
  }

  // ─── 3. Le formulaire, déjà rempli ─────────────────────────────────────────
  const justificatif = (
    <>
      <header className="flex items-center justify-between gap-2">
        <h1 className="font-titre text-lg font-bold text-nuit">Nouvelle dépense</h1>
      </header>

      {prefill && prefill.messages.length > 0 && (
        <section className="space-y-2">
          {prefill.messages.map((m) => (
            <p
              key={m}
              className="rounded-md border border-ciel/60 border-l-4 border-l-ciel p-3 text-sm text-nuit"
            >
              {m}
            </p>
          ))}
        </section>
      )}

      {/* R-CTL-01 : la raison est dite tout de suite, avec la sortie : remplacer
          la pièce : et non découverte au refus de la RH deux semaines plus tard. */}
      {risque && (
        <p className="rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond p-3 text-sm text-jaune">
          {risque} Ce justificatif risque d’être refusé : vous pouvez le remplacer par un document
          plus complet.
        </p>
      )}

      <section className="rounded-lg border border-nuit/10 bg-white p-4">
        <h2 className="font-titre text-sm font-bold text-nuit">Justificatif</h2>
        <p className="mb-2 text-xs text-nuit/60">
          {pieces.length === 0
            ? 'Aucun justificatif pour l’instant.'
            : 'Plusieurs photos d’un même ticket sont assemblées en un seul document.'}
        </p>

        {champFichierCache}
        <BoutonSecondaire
          type="button"
          disabled={preparation}
          onClick={() => champFichier.current?.click()}
        >
          {preparation
            ? 'Préparation…'
            : pieces.length
              ? 'Ajouter une photo'
              : 'Photographier le ticket'}
        </BoutonSecondaire>

        {pieces.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-2">
            {pieces.map((p, i) => (
              <li key={i} className="relative">
                {p.mime.startsWith('image/') ? (
                  <img src={apercuDe(p)} alt="" className="h-20 w-20 rounded object-cover" />
                ) : (
                  <span className="flex h-20 w-20 items-center justify-center rounded bg-ciel/30 text-xs text-nuit">
                    PDF
                  </span>
                )}
                <button
                  type="button"
                  aria-label="Retirer"
                  onClick={() => setPieces((liste) => liste.filter((_, j) => j !== i))}
                  className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-nuit text-xs text-white"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );

  return (
    <FormulaireDepense
      valeurInitiale={prefill?.champs ?? champsVides()}
      categories={categories}
      erreurs={erreurs}
      enCours={enregistrer.isPending}
      libelleValider="Enregistrer la dépense"
      avantChamps={justificatif}
      onAnnuler={() => navigate(-1)}
      onSoumettre={(champs) => enregistrer.mutate(champs)}
      apresActions={
        pieces.length === 0 ? (
          <p className="text-center text-xs text-nuit/60">
            Sans justificatif, la dépense sera enregistrée mais votre responsable pourra vous le
            réclamer.
          </p>
        ) : null
      }
    />
  );
}
